import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "#components/atoms/Checkbox/Checkbox";
import {
  InputDimensions,
  type DimensionKey,
} from "#components/atoms/InputDimensions/InputDimensions";
import { Field } from "#components/molecules/Field/Field";
import { InputUnit } from "#components/molecules/InputUnit/InputUnit";
import { Modal } from "#components/molecules/Modal/Modal";
import { Select } from "#components/molecules/Select/Select";
import { useCompany } from "#modules/company/view/useCompany";
import { useUpdateCompanySetting } from "#modules/company/view/useUpdateCompanySetting";
import { useUpdateMaterialLayout } from "#modules/materialLayouts/view/useUpdateMaterialLayout";
import { useCreateMaterial } from "#modules/materials/view/useCreateMaterial";
import { getUnitFactorsByMaterialType } from "#modules/quoteRequestLines/domain/getUnitFactors";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { Stack } from "#shared/view/components/Stack";
import { Factor } from "#shared/view/designSystem/inputs/units";
import {
  FormatterMaterialParameter,
  getMaterialDensity,
  getMaterialDimensionsFieldsConfiguration,
  getMaterialTypesFromPartType,
  MaterialGradeType,
  MaterialLayout,
  MaterialTypeSchema,
  MaterialTypeType,
  PartTypeType,
  round,
} from "shared";

type Props = {
  opened: boolean;
  onClose: () => void;
  onFormatSaved: (materialId: string | null) => void;
  materialLayout: MaterialLayout;
  materialGrade: MaterialGradeType;
  materialName: string;
  partType: PartTypeType | null;
  partSubType: string | null;
  quoteRequestLineId: string | undefined;
};

type DimensionsState = Record<"height" | "width" | "length", number | undefined> & {
  tubeThickness: number | undefined;
};

const RAW_UNIT_FACTOR = { value: 1, unit: "€" } as const satisfies Factor;

const DIMENSION_HANDLER_BY_KEY = {
  thickness: "onThicknessChange",
  diameter: "onDiameterChange",
  height: "onHeightChange",
  width: "onWidthChange",
  length: "onLengthChange",
} as const satisfies Record<DimensionKey, string>;

export const AddMaterialFormatModal = ({
  opened,
  onClose,
  onFormatSaved,
  materialLayout,
  materialGrade,
  materialName,
  partType,
  partSubType,
  quoteRequestLineId,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: createMaterial, isPending: isCreating } = useCreateMaterial();
  const { updateMaterialLayoutAsync, isPending: isUpdating } = useUpdateMaterialLayout({
    quoteRequestLineId,
  });
  const { company } = useCompany();
  const { updateCompanySetting } = useUpdateCompanySetting({ shouldNotNotify: true });

  const materialTypes = partType
    ? getMaterialTypesFromPartType(partType)
    : Object.values(MaterialTypeSchema.enum);
  const materialType: MaterialTypeType | null = materialTypes[0] ?? null;
  const [dimensions, setDimensions] = useState<DimensionsState>({
    height: undefined,
    width: undefined,
    length: undefined,
    tubeThickness: undefined,
  });
  const [isAddedToCatalog, setIsAddedToCatalog] = useState(false);
  const [price, setPrice] = useState("");
  const [chosenPriceUnit, setChosenPriceUnit] = useState<string | null>(null);

  const fieldsConfig = getMaterialDimensionsFieldsConfiguration(partType);
  const hasAllDimensions = fieldsConfig.every(({ field }) => Boolean(dimensions[field]));

  const priceUnitFactors = [
    RAW_UNIT_FACTOR,
    ...getPriceUnitFactors({
      partType: materialLayout.type ?? partType,
      density: materialLayout.density || getMaterialDensity({ materialGrade }),
      dimensions,
    }),
  ];
  const selectedPriceUnit = chosenPriceUnit ?? company?.companySettings.defaultMaterialPriceUnit;
  const priceUnitFactor =
    priceUnitFactors.find(({ unit }) => unit === selectedPriceUnit) ?? RAW_UNIT_FACTOR;
  const parsedPrice = parsePrice(price);
  const priceInCents =
    parsedPrice === null ? null : round((parsedPrice / priceUnitFactor.value) * 100);

  const changePriceUnit = (unit: string) => {
    setChosenPriceUnit(unit);
    updateCompanySetting({ defaultMaterialPriceUnit: unit });
  };

  const updateDimension = (
    field: keyof FormatterMaterialParameter,
    syncWith: keyof FormatterMaterialParameter | undefined,
    value: number | undefined,
  ) =>
    setDimensions((previous) => ({
      ...previous,
      [field]: value,
      ...(syncWith ? { [syncWith]: value } : {}),
    }));

  const inputDimensionsProps = Object.fromEntries(
    fieldsConfig.flatMap(({ key, field, syncWith }) => [
      [key, dimensions[field]],
      [
        DIMENSION_HANDLER_BY_KEY[key],
        (value: number | undefined) => updateDimension(field, syncWith, value),
      ],
    ]),
  );

  const save = async () => {
    if (priceInCents === null) {
      return;
    }
    const geometry = {
      height: dimensions.height ?? 0,
      width: dimensions.width ?? 0,
      length: dimensions.length ?? 0,
      tubeThickness: dimensions.tubeThickness ?? null,
    };

    if (isAddedToCatalog && materialType) {
      const createdMaterial = await createMaterial({
        name: materialName,
        type: materialType,
        subType: partSubType,
        density: materialLayout.density,
        materialGrade,
        price: priceInCents,
        ...geometry,
      });
      onFormatSaved(createdMaterial.id);
      onClose();
      return;
    }

    await updateMaterialLayoutAsync({
      id: materialLayout.id,
      payload: { data: { ...materialLayout, ...geometry, price: priceInCents, materialId: null } },
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.layouts.all });
    onFormatSaved(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      title={t("layout.formatModal.title")}
      primaryText={t("layout.sidePanel.save")}
      closeText={t("layout.sidePanel.cancel")}
      primaryDisabled={!hasAllDimensions || priceInCents === null}
      primaryLoading={isCreating || isUpdating}
      onClose={onClose}
      onPrimary={save}
    >
      <Stack gap={20}>
        <Select
          label={t("layout.formatModal.materialType")}
          data={materialTypes.map((type) => ({
            value: type,
            label: t(`qrl.materialTypes.${type}`),
          }))}
          value={materialType}
          searchable={false}
          disabled
        />
        <Field label={t("layout.formatModal.dimensions")}>
          <InputDimensions
            dimensions={fieldsConfig.map(({ key }) => key)}
            clearable
            autoFocus
            {...inputDimensionsProps}
          />
        </Field>
        <Field label={t("layout.formatModal.price")}>
          <InputUnit
            value={price}
            onChange={setPrice}
            unit={priceUnitFactor.unit}
            units={priceUnitFactors.map(({ unit }) => unit)}
            onUnitChange={changePriceUnit}
            error={Boolean(price) && parsedPrice === null}
            aria-label={t("layout.formatModal.price")}
          />
        </Field>
        <Checkbox
          label={t("layout.formatModal.addToCatalog")}
          checked={isAddedToCatalog}
          onChange={(event) => setIsAddedToCatalog(event.currentTarget.checked)}
        />
      </Stack>
    </Modal>
  );
};

const getPriceUnitFactors = ({
  partType,
  density,
  dimensions,
}: {
  partType: PartTypeType | null;
  density: number;
  dimensions: DimensionsState;
}) =>
  partType
    ? (getUnitFactorsByMaterialType({
        partType,
        material: {
          densityKgDm3: density,
          height: dimensions.height ?? 0,
          width: dimensions.width ?? 0,
          length: dimensions.length ?? 0,
          tubeThickness: dimensions.tubeThickness ?? 0,
        },
      }) ?? [])
    : [];

const parsePrice = (value: string) => {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value.replace(",", "."));
  return Number.isNaN(parsed) || parsed < 0 ? null : parsed;
};
